import { useState, useEffect, useCallback } from "react";
import Swipeable from "@/components/Swipeable";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Home,
  Search,
  FlaskConical,
  Building2,
  Star,
  ChevronDown,
  ChevronRight,
  Loader2,
  CheckCircle,
  AlertCircle,
  Sparkles,
  FileText,
  RefreshCw,
  Info,
  Award,
  X,
  Edit,
  Save,
  BookOpen,
  ScrollText,
  Menu
} from "lucide-react";
import { authGet, authPost, authPut } from "@/utils/auth";
import { toast } from "sonner";
import MobileBottomNav from "@/components/MobileBottomNav";
import MobileSidebar from "@/components/MobileSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Rating stars component
const RatingStars = ({ rating, size = "sm" }) => {
  const starSize = size === "sm" ? "h-3 w-3" : "h-4 w-4";
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${starSize} ${star <= rating ? "text-[#A87608] fill-[#D4A017]" : "text-[#3A4557]"}`}
        />
      ))}
    </div>
  );
};

// Feature rating badge
const RatingBadge = ({ rating }) => {
  const colors = {
    5: "bg-emerald-500/20 text-emerald-600 border-emerald-500/30",
    4: "bg-[#EDF1F8]/40 text-[#2E3B57] border-blue-500/30",
    3: "bg-[#D4A017]/15 text-[#A87608] border-[#D4A017]/30",
    2: "bg-orange-500/20 text-orange-600 border-orange-500/30",
    1: "bg-red-500/20 text-red-600 border-red-500/30"
  };
  
  const labels = {
    5: "Excellent",
    4: "Good",
    3: "Average",
    2: "Below Avg",
    1: "Poor"
  };
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs border ${colors[rating] || colors[3]}`}>
      {labels[rating] || "N/A"}
    </span>
  );
};

export default function PolicyResearch() {
  const navigate = useNavigate();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedFeature, setExpandedFeature] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);

  // Fetch research data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authGet(`${API}/research/features`);
      if (response.ok) {
        setFeatures(response.data.features || []);
      }
    } catch (error) {
      console.error("Error fetching research data:", error);
      toast.error("Failed to load research data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Analyze documents (background task with polling)
  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const response = await authPost(`${API}/research/analyze-documents`);
      if (response.ok) {
        toast.success("Analysis started! Processing documents in background...");
        // Poll for status
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await authGet(`${API}/research/analyze-status`);
            if (statusRes.ok) {
              const s = statusRes.data;
              if (s.status === "complete") {
                clearInterval(pollInterval);
                toast.success(`Analysis complete! ${s.analyzed || 0} features extracted.`);
                setAnalyzing(false);
                fetchData();
              } else if (s.status === "error") {
                clearInterval(pollInterval);
                toast.error("Analysis failed: " + (s.error || "Unknown error"));
                setAnalyzing(false);
              } else if (s.status === "running" && s.progress) {
                toast.info(`Analyzing: ${s.progress.current || "..."} (${s.progress.done || 0}/${s.progress.total || "?"})`, { id: "analyze-progress" });
              }
            }
          } catch (e) { /* ignore polling errors */ }
        }, 5000);
        // Safety timeout: stop polling after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (analyzing) {
            setAnalyzing(false);
            toast.info("Analysis is still running in background. Refresh to see results.");
            fetchData();
          }
        }, 300000);
      } else {
        throw new Error(response.data?.detail || "Analysis failed");
      }
    } catch (error) {
      toast.error(error.message);
      setAnalyzing(false);
    }
  };

  // Save edited entry
  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    
    try {
      const response = await authPut(
        `${API}/research/feature/${editingEntry.feature_id}/company/${editingEntry.company_id}`,
        editingEntry
      );
      if (response.ok) {
        toast.success("Research entry updated!");
        setEditingEntry(null);
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to save changes");
    }
  };

  // Filter features
  const filteredFeatures = features.filter(f =>
    searchQuery === "" ||
    f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get best company for a feature
  const getBestCompany = (feature) => {
    const companies = feature.company_data || [];
    if (companies.length === 0) return null;
    return companies.reduce((best, curr) => 
      (curr.rating || 0) > (best.rating || 0) ? curr : best
    , companies[0]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F6FA] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-[#3A4557]">Loading research data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6FA] text-[#1B2A4A] pb-24 md:pb-0">
      {/* Mobile Sidebar */}
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />

      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-40 bg-[#F4F6FA]/95 backdrop-blur-sm border-b border-[#C9D4E6]">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSidebar(true)}
              className="p-2 -ml-2 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] active:bg-[#E2E9F4] transition-colors"
            >
              <Menu className="h-5 w-5 text-[#1B2A4A]" />
            </button>
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <FlaskConical className="h-5 w-5 text-[#1B2A4A]" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Smart Research</h1>
              <p className="text-xs text-[#3A4557]">Policy Analysis</p>
            </div>
          </div>
          
          <Button
            onClick={handleAnalyze}
            disabled={analyzing}
            size="sm"
            className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 rounded-xl"
          >
            {analyzing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
          </Button>
        </div>
        
        {/* Mobile Search */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search features..."
              className="w-full pl-10 pr-4 py-2.5 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] placeholder:text-[#3A4557] text-sm"
            />
          </div>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="hidden md:block sticky top-0 z-40 bg-[#F4F6FA]/95 backdrop-blur-sm border-b border-[#C9D4E6]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-[#EDF1F8] rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigate("/advisor/dashboard")}
              className="p-2 hover:bg-[#EDF1F8] rounded-lg transition-colors"
            >
              <Home className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <FlaskConical className="h-5 w-5 text-[#1B2A4A]" />
              </div>
              <div>
                <h1 className="text-lg font-bold">Smart Research</h1>
                <p className="text-xs text-[#3A4557]">Policy Feature Analysis</p>
              </div>
            </div>
          </div>
          
          <Button
            onClick={handleAnalyze}
            disabled={analyzing}
            className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
          >
            {analyzing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Analyze Documents
              </>
            )}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 md:py-6">
        {/* Info Banner - Hidden on mobile */}
        <div className="hidden md:block mb-6 p-4 bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border border-purple-500/30 rounded-xl">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-purple-700 font-medium">How Smart Research Works</p>
              <p className="text-xs text-[#3A4557] mt-1">
                Upload policy documents to the Document Library → Click "Analyze Documents" → AI reads every line and extracts feature data → Compare coverage across companies instantly
              </p>
            </div>
          </div>
        </div>

        {/* Search - Desktop Only (Mobile has it in header) */}
        <div className="hidden md:block relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557]" />
          <input
            type="text"
            placeholder="Search features (Room Rent, NCB, Waiting Period...)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] placeholder-[#9AA3B2] focus:outline-none focus:ring-2 focus:ring-purple-500/50"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <FlaskConical className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{features.length}</p>
                <p className="text-xs text-[#3A4557]">Features</p>
              </div>
            </div>
          </div>
          <div className="bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-[#EDF1F8]/40 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-[#2E3B57]" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {new Set(features.flatMap(f => f.company_data?.map(c => c.company_id) || [])).size}
                </p>
                <p className="text-xs text-[#3A4557]">Companies</p>
              </div>
            </div>
          </div>
          <div className="bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <FileText className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {features.reduce((sum, f) => sum + (f.company_data?.length || 0), 0)}
                </p>
                <p className="text-xs text-[#3A4557]">Data Points</p>
              </div>
            </div>
          </div>
          <div className="bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-[#D4A017]/15 flex items-center justify-center">
                <Award className="h-5 w-5 text-[#A87608]" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {features.filter(f => f.company_data?.length > 0).length}
                </p>
                <p className="text-xs text-[#3A4557]">Analyzed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Features List */}
        {filteredFeatures.length === 0 ? (
          <div className="text-center py-16 bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-xl">
            <FlaskConical className="h-16 w-16 text-[#3A4557] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#3A4557] mb-2">No Research Data Yet</h3>
            <p className="text-[#3A4557] mb-6">Upload policy documents and click "Analyze Documents" to start</p>
            <Button
              onClick={() => navigate("/advisor/documents")}
              variant="outline"
              className="border-purple-500/50 text-purple-600 hover:bg-purple-500/10"
            >
              <FileText className="h-4 w-4 mr-2" />
              Go to Document Library
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredFeatures.map((feature) => {
              const bestCompany = getBestCompany(feature);
              const isExpanded = expandedFeature === feature.id;
              
              return (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-xl overflow-hidden"
                >
                  {/* Feature Header */}
                  <button
                    onClick={() => setExpandedFeature(isExpanded ? null : feature.id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-[#E2E9F4]/30 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                        <FlaskConical className="h-6 w-6 text-[#1B2A4A]" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-[#1B2A4A]">{feature.name}</h3>
                        <p className="text-xs text-[#3A4557] mt-0.5">{feature.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {bestCompany && (
                        <div className="text-right hidden sm:block">
                          <p className="text-xs text-[#3A4557]">Best Coverage</p>
                          <p className="text-sm text-emerald-600 font-medium flex items-center gap-1">
                            <Award className="h-3 w-3" />
                            {bestCompany.company_name}
                          </p>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-[#E2E9F4]/50 rounded text-xs text-[#3A4557]">
                          {feature.company_data?.length || 0} companies
                        </span>
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-[#3A4557]" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-[#3A4557]" />
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Expanded Content */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-[#C9D4E6]/50"
                      >
                        <div className="p-4">
                          {/* Best Coverage Info */}
                          <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                            <p className="text-xs text-emerald-600 font-medium mb-1">What's the Best Coverage?</p>
                            <p className="text-sm text-[#2E3B57]">{feature.best_coverage}</p>
                          </div>

                          {/* Company Comparison Table */}
                          {feature.company_data?.length > 0 ? (
                            <Swipeable hint>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-[#C9D4E6]">
                                    <th className="text-left py-2 px-3 text-[#3A4557] font-medium">Company</th>
                                    <th className="text-left py-2 px-3 text-[#3A4557] font-medium">Base Coverage</th>
                                    <th className="text-left py-2 px-3 text-[#3A4557] font-medium">With Rider</th>
                                    <th className="text-left py-2 px-3 text-[#3A4557] font-medium">Rating</th>
                                    <th className="text-left py-2 px-3 text-[#3A4557] font-medium">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {feature.company_data
                                    .sort((a, b) => (b.rating || 0) - (a.rating || 0))
                                    .map((company, idx) => (
                                      <tr key={idx} className="border-b border-[#C9D4E6] hover:bg-[#E2E9F4]/20">
                                        <td className="py-3 px-3">
                                          <div className="flex items-center gap-2">
                                            {idx === 0 && feature.company_data.length > 1 && (
                                              <Award className="h-4 w-4 text-[#A87608]" />
                                            )}
                                            <span className="text-[#1B2A4A] font-medium">{company.company_name}</span>
                                          </div>
                                        </td>
                                        <td className="py-3 px-3">
                                          <span className="text-[#2E3B57]">{company.base_coverage || "-"}</span>
                                        </td>
                                        <td className="py-3 px-3">
                                          {company.with_rider ? (
                                            <div>
                                              <span className="text-[#2E3B57]">{company.with_rider}</span>
                                              {company.rider_name && (
                                                <p className="text-xs text-[#3A4557]">({company.rider_name})</p>
                                              )}
                                            </div>
                                          ) : (
                                            <span className="text-[#3A4557]">-</span>
                                          )}
                                        </td>
                                        <td className="py-3 px-3">
                                          <div className="flex items-center gap-2">
                                            <RatingStars rating={company.rating || 3} />
                                            <RatingBadge rating={company.rating || 3} />
                                          </div>
                                        </td>
                                        <td className="py-3 px-3">
                                          <div className="flex items-center gap-2">
                                            <button
                                              onClick={() => setSelectedCompany(company)}
                                              className="text-purple-600 hover:text-purple-700 text-xs flex items-center gap-1"
                                            >
                                              View Details
                                              {company.policy_wording_exact && (
                                                <ScrollText className="h-3 w-3 text-cyan-600" title="Policy wording available" />
                                              )}
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </Swipeable>
                          ) : (
                            <div className="text-center py-8 text-[#3A4557]">
                              <FlaskConical className="h-8 w-8 mx-auto mb-2 opacity-50" />
                              <p>No data yet. Analyze documents to extract feature data.</p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>

      {/* Company Detail Modal */}
      <AnimatePresence>
        {selectedCompany && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setSelectedCompany(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white border border-[#C9D4E6] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-[#C9D4E6] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#EDF1F8] to-indigo-600 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-[#1B2A4A]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold">{selectedCompany.company_name}</h2>
                    <p className="text-xs text-[#3A4557]">Feature Details</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedCompany(null)}
                  className="p-2 hover:bg-[#EDF1F8] rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Rating */}
                <div className="flex items-center justify-between p-4 bg-[#EDF1F8]/50 rounded-xl">
                  <span className="text-[#3A4557]">Rating</span>
                  <div className="flex items-center gap-2">
                    <RatingStars rating={selectedCompany.rating || 3} size="md" />
                    <RatingBadge rating={selectedCompany.rating || 3} />
                  </div>
                </div>

                {/* Base Coverage */}
                <div className="p-4 bg-[#EDF1F8]/50 rounded-xl">
                  <p className="text-xs text-[#3A4557] mb-1">Base Coverage</p>
                  <p className="text-[#1B2A4A]">{selectedCompany.base_coverage || "Not specified"}</p>
                </div>

                {/* With Rider */}
                {selectedCompany.with_rider && (
                  <div className="p-4 bg-[#EDF1F8]/30 border border-blue-500/30 rounded-xl">
                    <p className="text-xs text-[#2E3B57] mb-1">
                      With Rider {selectedCompany.rider_name && `(${selectedCompany.rider_name})`}
                    </p>
                    <p className="text-[#1B2A4A]">{selectedCompany.with_rider}</p>
                  </div>
                )}

                {/* Exact Policy Wording - NEW SECTION */}
                {selectedCompany.policy_wording_exact && (
                  <div className="p-4 bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-purple-500/30 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <ScrollText className="h-4 w-4 text-purple-600" />
                      <p className="text-sm text-purple-600 font-medium">Exact Policy Wording</p>
                    </div>
                    <div className="bg-white/50 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <p className="text-[#2E3B57] text-sm whitespace-pre-wrap font-mono leading-relaxed">
                        {selectedCompany.policy_wording_exact}
                      </p>
                    </div>
                    <p className="text-xs text-[#3A4557] mt-2 italic">
                      This is the verbatim text extracted from the policy document
                    </p>
                  </div>
                )}

                {/* Related Definitions - NEW SECTION */}
                {selectedCompany.definitions && selectedCompany.definitions.length > 0 && (
                  <div className="p-4 bg-gradient-to-br from-cyan-500/10 to-[#DCE4F2]/10 border border-cyan-500/30 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <BookOpen className="h-4 w-4 text-cyan-600" />
                      <p className="text-sm text-cyan-600 font-medium">Related Definitions</p>
                    </div>
                    <div className="space-y-3">
                      {selectedCompany.definitions.map((def, idx) => (
                        <div key={idx} className="bg-white/50 rounded-lg p-3">
                          <p className="text-[#1B2A4A] font-medium text-sm mb-1">"{def.term}"</p>
                          <p className="text-[#3A4557] text-xs whitespace-pre-wrap">{def.definition}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Terms & Conditions */}
                {selectedCompany.terms_conditions && (
                  <div className="p-4 bg-[#EDF1F8]/50 rounded-xl">
                    <p className="text-xs text-[#3A4557] mb-1">Terms & Conditions</p>
                    <p className="text-[#2E3B57] text-sm whitespace-pre-wrap">{selectedCompany.terms_conditions}</p>
                  </div>
                )}

                {/* Limitations */}
                {selectedCompany.limitations && (
                  <div className="p-4 bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl">
                    <p className="text-xs text-[#A87608] mb-1">Limitations / Exclusions</p>
                    <p className="text-[#2E3B57] text-sm whitespace-pre-wrap">{selectedCompany.limitations}</p>
                  </div>
                )}

                {/* Rating Reason */}
                {selectedCompany.rating_reason && (
                  <div className="p-4 bg-[#EDF1F8]/50 rounded-xl">
                    <p className="text-xs text-[#3A4557] mb-1">Why This Rating?</p>
                    <p className="text-[#2E3B57] text-sm">{selectedCompany.rating_reason}</p>
                  </div>
                )}

                {/* Source */}
                {selectedCompany.source_document && (
                  <div className="text-xs text-[#3A4557] flex items-center gap-2">
                    <FileText className="h-3 w-3" />
                    Source: {selectedCompany.source_document}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
    </div>
  );
}
